import { Component } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { FormlyFormOptions, FormlyFieldConfig } from '@ngx-formly/core';
import { MatTableDataSource } from '@angular/material/table';
import { CurrencyPipe, PercentPipe, DecimalPipe } from '@angular/common';

export interface Statistic {
  metric: string;
  need: string;
  have: string;
}
const STATISTIC_DATA: Statistic[] = [
  { metric: 'Rate of Return', need: 'N/A', have: '5.50' },
  { metric: 'Standard Deviation', need: 'N/A', have: '6.50' },
  { metric: 'Variance Drag Phantom Tax', need: 'N/A', have: '0.89' },
  { metric: 'Sharpe Ratio', need: 'N/A', have: '1.13' },
  { metric: 'Probability of Any Loss in the Next 12 Months', need: 'N/A', have: '27.50' },
  { metric: 'Amount of Money at Risk in the Next 12 Months', need: 'N/A', have: '205000.50' },
  { metric: 'Upper Return', need: 'N/A', have: '18.50' },
  { metric: 'Lower Return', need: 'N/A', have: '-7.50' },
];

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent {
  title = 'Distress Calculator';
  form = new FormGroup({});
  model: any = {};
  options: FormlyFormOptions = {};
  fields: FormlyFieldConfig[] = [
    {
      key: 'startingValue',
      id: 'startingValue',
      type: 'input',
      templateOptions: {
        label: 'Starting Value',
        placeholder: 'Enter a Value',
        description: 'The total value of the portfolio',
        required: true,
        blur: (formlyFieldConfig: FormlyFieldConfig) => {
          if (this.model.startingValue){
            let value: string = (this.model.startingValue as string).replace(/[^0-9.-]+/g,'');
            formlyFieldConfig.parent.fieldGroup.find(k => k.key === 'startingValue').formControl.patchValue(this.currencyPipe.transform(value, 'USD'));
          }
        }
      },
      modelOptions: {
        updateOn: 'blur'
      }
    },
    {
      key: 'rateOfReturn',
      id: 'rateOfReturn',
      type: 'input',
      templateOptions: {
        label: 'Rate of Return',
        placeholder: 'Enter a Value',
        description: 'The expected rate of return of the portfolio',
        required: true,
        blur: (formlyFieldConfig: FormlyFieldConfig) => {
          if(this.model.rateOfReturn) {
            let value: string = (this.model.rateOfReturn as string).replace(/[^0-9.-]+/g,'');
            value = (parseFloat(value)/100).toString();
            if (value && value !== 'NaN'){
              formlyFieldConfig.parent.fieldGroup.find(k => k.key === 'rateOfReturn').formControl.patchValue(this.percentPipe.transform(value, '1.0-2'));
            } else {
              formlyFieldConfig.parent.fieldGroup.find(k => k.key === 'rateOfReturn').formControl.patchValue('');
            }
          }
        }
      },
      modelOptions: {
        updateOn: 'blur'
      }
    },
    {
      key: 'standardDeviation',
      id: 'standardDeviation',
      type: 'input',
      templateOptions: {
        label: 'Standard Deviation',
        placeholder: 'Enter a Value',
        description: 'The standard deviation of the portfolio',
        required: true,
        blur: (formlyFieldConfig: FormlyFieldConfig) => {
          if(this.model.standardDeviation) {
            let value: string = (this.model.standardDeviation as string).replace(/[^0-9.-]+/g,'');
            value = (parseFloat(value)/100).toString();
            if (value && value !== 'NaN'){
              formlyFieldConfig.parent.fieldGroup.find(k => k.key === 'standardDeviation').formControl.patchValue(this.percentPipe.transform(value, '1.0-2'));
            } else {
              formlyFieldConfig.parent.fieldGroup.find(k => k.key === 'standardDeviation').formControl.patchValue('');
            }
          }
        }
      },
      modelOptions: {
        updateOn: 'blur'
      }
    },
  ];
  displayedColumns: string[] = ['metric', 'need'];
  dataSource: MatTableDataSource<Statistic> = new MatTableDataSource<Statistic>(STATISTIC_DATA);
  calculateMetrics(){
    let statistic: Statistic[] = [
      { metric: 'Rate of Return', need: this.model.rateOfReturn,  have: '5.50' },
      { metric: 'Standard Deviation', need: this.model.standardDeviation, have: '6.50' },
      { metric: 'Variance Drag Phantom Tax', need: this.decimalPipe.transform(parseFloat( this.model.rateOfReturn)/parseFloat(this.model.standardDeviation), '1.0-2'), have: '0.89' },
      { metric: 'Sharpe Ratio', need: this.decimalPipe.transform((parseFloat( this.model.rateOfReturn) - 1)/parseFloat(this.model.standardDeviation), '1.0-2'), have: '1.13' },
      { metric: 'Probability of Any Loss in the Next 12 Months', need: this.percentPipe.transform(this.calculate_probability_loss_in_12_months(parseFloat(this.model.standardDeviation), parseFloat(this.model.rateOfReturn)), '1.0-2'), have: '27.50' },
      { metric: 'Amount of Money at Risk in the Next 12 Months', need: this.currencyPipe.transform(this.calculate_amount_money_risk_in_12_months(parseFloat(this.model.standardDeviation), parseFloat(this.model.rateOfReturn), parseFloat((this.model.startingValue as string).replace(/[^0-9.-]+/g,''))), 'USD'), have: '205000.50' },
      { metric: 'Upper Return', need: this.percentPipe.transform((parseFloat(this.model.rateOfReturn) + 2 * parseFloat(this.model.standardDeviation)) / 100, '1.0-2'), have: '18.50' },
      { metric: 'Lower Return', need: this.percentPipe.transform((parseFloat(this.model.rateOfReturn) - 2 * parseFloat(this.model.standardDeviation)) / 100, '1.0-2'), have: '-7.50' },
    ];
    this.dataSource = new MatTableDataSource<Statistic>(statistic);
  }

  /**
   * Calculates the ERF function
   * Returns the value from the ERF function
   * @param {*} x The value to pass into the ERF function
   */
  erf(x) {
    // constants
    var a1 =  0.254829592;
    var a2 = -0.284496736;
    var a3 =  1.421413741;
    var a4 = -1.453152027;
    var a5 =  1.061405429;
    var p  =  0.3275911;

    // Save the sign of x
    var sign = 1;
    if (x < 0) {
        sign = -1;
    }
    x = Math.abs(x);

    // A&S formula 7.1.26
    var t = 1.0/(1.0 + p*x);
    var y = 1.0 - (((((a5*t + a4)*t) + a3)*t + a2)*t + a1)*t*Math.exp(-x*x);

    return sign*y;
}

  /**
   * Calculates the inverse ERF function
   * Returns the value from the inverse ERF function
   * @param {*} x The value to pass into the InvERF function
   */
  erfinv(x){
    var z;
    var a  = 0.147;                                                   
    var the_sign_of_x;
    if(0==x) {
        the_sign_of_x = 0;
    } else if(x>0){
        the_sign_of_x = 1;
    } else {
        the_sign_of_x = -1;
    }

    if(0 != x) {
        var ln_1minus_x_sqrd = Math.log(1-x*x);
        var ln_1minusxx_by_a = ln_1minus_x_sqrd / a;
        var ln_1minusxx_by_2 = ln_1minus_x_sqrd / 2;
        var ln_etc_by2_plus2 = ln_1minusxx_by_2 + (2/(Math.PI * a));
        var first_sqrt = Math.sqrt((ln_etc_by2_plus2*ln_etc_by2_plus2)-ln_1minusxx_by_a);
        var second_sqrt = Math.sqrt(first_sqrt - ln_etc_by2_plus2);
        z = second_sqrt * the_sign_of_x;
    } else { // x is zero
        z = 0;
    }
    return z;
  }

  /**
     * Calculates the CDF given a value on the distribution
     * Returns the value from the CDF funtion
     * @param {*} x The value passed
     * @param {*} mean The average of the distribution
     * @param {*} standard_deviation The standard deviation of the distribution
     */
    cdf(x, mean, standard_deviation) {
      return (1/2) * (1 + this.erf( (x - mean)/(standard_deviation*Math.sqrt(2)) ))
  }

  /**
     * Calculates the Inverse CDF given a value on the distribution
     * Returns the value from the Inverse CDF funtion
     * @param {*} p The value passed
     * @param {*} mean The average of the distribution
     * @param {*} standard_deviation The standard deviation of the distribution
     */
    inv_cdf(p, mean, standard_deviation) {
      return mean + standard_deviation*Math.sqrt(2)*this.erfinv(2 * p - 1);
  }

  /**
     * Calculates the probability of loss in 12 months for the portfolio
     * Returns the prbability of any loss 
     * @param {*} standard_deviation The standard deviation of the portfolio
     * @param {*} rate_of_return The rate of return of the portfolio
     */
    calculate_probability_loss_in_12_months(standard_deviation, rate_of_return){
      return this.cdf(0, rate_of_return, standard_deviation);
  }

  /**
     * Calculates the amount of money at risk in 12 months for the portfolio
     * Returns the amount of money at risk
     * @param {*} standard_deviation The standard deviation of the portfolio
     * @param {*} rate_of_return The rate of return of the portfolio
     * @param {*} starting_value The initial value of the portfolio
     */
    calculate_amount_money_risk_in_12_months(standard_deviation, rate_of_return, starting_value){
      let inverse_normal = this.inv_cdf(.99, 0.0, 1.0);
      
      let amount_money_risk = (((inverse_normal * standard_deviation) - rate_of_return) * starting_value/100);
      if(amount_money_risk < 0.0){
          amount_money_risk = 0.0;
      }
      // console.log('++++++++++++++++++++++++++++++++++++++++++++++++');
      // console.log('Inverse Normal is: ' + inverse_normal);
      // console.log('Standard Deviation is: ' + standard_deviation);
      // console.log('Rate of Return is: ' + rate_of_return);
      // console.log('Starting Value is: ' + starting_value);
      // console.log('Money at Risk is: ' + starting_value);
      // console.log('++++++++++++++++++++++++++++++++++++++++++++++++');
      return amount_money_risk;
  }

  constructor(public currencyPipe: CurrencyPipe, public percentPipe: PercentPipe, public decimalPipe: DecimalPipe) {}
}
